<script lang="ts">
  import { lists, createList, renameList, deleteList } from "../stores/data";
  import { currentUser, logout } from "../stores/auth";
  import DarkModeToggle from "./DarkModeToggle.svelte";

  let {
    selectedListId,
    onSelectList,
    onNavigate,
    onClose,
  }: {
    selectedListId: string | null;
    onSelectList: (id: string) => void;
    onNavigate: (page: "admin" | "account") => void;
    onClose: () => void;
  } = $props();

  let newListName = $state("");
  let renamingId = $state<string | null>(null);
  let renameValue = $state("");

  function addList(event: SubmitEvent): void {
    event.preventDefault();
    const trimmed = newListName.trim();
    if (!trimmed) return;
    void createList(trimmed).then((list) => onSelectList(list.id));
    newListName = "";
  }

  function startRename(id: string, currentName: string, event: MouseEvent): void {
    event.stopPropagation();
    renamingId = id;
    renameValue = currentName;
  }

  function saveRename(event: SubmitEvent): void {
    event.preventDefault();
    if (renamingId && renameValue.trim()) {
      void renameList(renamingId, renameValue.trim());
    }
    renamingId = null;
  }

  function remove(id: string, event: MouseEvent): void {
    event.stopPropagation();
    if (confirm("Delete this list and all its items?")) {
      void deleteList(id);
      if (selectedListId === id) onSelectList("");
    }
  }

  async function handleLogout(): Promise<void> {
    await logout();
    onClose();
  }
</script>

<div class="backdrop" onclick={onClose} role="presentation"></div>
<nav class="drawer">
  <div class="header">
    <strong>{$currentUser?.username}</strong>
    <DarkModeToggle />
  </div>

  <ul class="list-of-lists">
    {#each $lists as list (list.id)}
      <li>
        {#if renamingId === list.id}
          <form onsubmit={saveRename} class="rename-form">
            <input type="text" bind:value={renameValue} maxlength="200" />
            <button type="submit">Save</button>
          </form>
        {:else}
          <button
            class="list-btn"
            class:active={list.id === selectedListId}
            onclick={() => {
              onSelectList(list.id);
              onClose();
            }}
            type="button"
          >
            {list.name}
          </button>
          <button class="icon-btn" aria-label="Rename list" onclick={(e) => startRename(list.id, list.name, e)} type="button">✎</button>
          <button class="icon-btn" aria-label="Delete list" onclick={(e) => remove(list.id, e)} type="button">🗑</button>
        {/if}
      </li>
    {/each}
  </ul>

  <form class="new-list" onsubmit={addList}>
    <input type="text" placeholder="New list…" bind:value={newListName} maxlength="200" />
    <button type="submit">+</button>
  </form>

  <div class="nav-links">
    <button type="button" onclick={() => onNavigate("account")}>Account</button>
    {#if $currentUser?.role === "admin"}
      <button type="button" onclick={() => onNavigate("admin")}>Admin</button>
    {/if}
    <button type="button" onclick={handleLogout}>Log out</button>
  </div>
</nav>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
  }
  .drawer {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(85vw, 320px);
    background: var(--surface);
    z-index: 11;
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.2);
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    padding-top: max(1rem, env(safe-area-inset-top));
    border-bottom: 1px solid var(--border);
  }
  .list-of-lists {
    list-style: none;
    margin: 0;
    padding: 0.5rem 0;
    overflow-y: auto;
    flex: 1;
  }
  .list-of-lists li {
    display: flex;
    align-items: center;
  }
  .list-btn {
    flex: 1;
    text-align: left;
    background: none;
    border: none;
    padding: 0.85rem 1rem;
    color: var(--text);
    font-size: 1rem;
    cursor: pointer;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .list-btn.active {
    color: var(--accent);
    font-weight: 600;
  }
  .icon-btn {
    background: none;
    border: none;
    padding: 0.5rem;
    color: var(--text-muted);
    cursor: pointer;
  }
  .rename-form {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    flex: 1;
  }
  .rename-form input {
    flex: 1;
    padding: 0.5rem;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--text);
  }
  .rename-form button {
    border: none;
    border-radius: var(--radius);
    background: var(--accent);
    color: var(--accent-contrast);
    padding: 0 0.75rem;
  }
  .new-list {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--border);
  }
  .new-list input {
    flex: 1;
    padding: 0.6rem;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--text);
  }
  .new-list button {
    border: none;
    border-radius: var(--radius);
    background: var(--accent);
    color: var(--accent-contrast);
    width: 2.5rem;
    font-size: 1.2rem;
    cursor: pointer;
  }
  .nav-links {
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--border);
    padding-bottom: env(safe-area-inset-bottom);
  }
  .nav-links button {
    background: none;
    border: none;
    text-align: left;
    padding: 0.9rem 1rem;
    color: var(--text);
    cursor: pointer;
    font-size: 0.95rem;
  }
</style>
